import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const LoginPage = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      
      if (!user.profile_complete) {
        navigate("/setup");
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-800 mb-8"
            data-testid="back-to-home"
          >
            ← {t('nav.backToHome')}
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <img src={LOGO_URL} alt="SingleSeen" className="h-32 w-auto" />
          </div>

          <h1 className="font-serif text-3xl text-rose-900 mb-2">{t('login.title')}</h1>
          <p className="text-rose-600 mb-8">{t('login.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-rose-700">{t('login.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white border-rose-200 focus:border-rose-500 focus:ring-rose-500/20 h-12"
                required
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-rose-700">{t('login.passwordLabel')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pr-16 bg-white border-rose-200 focus:border-rose-500 focus:ring-rose-500/20 h-12"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-400 hover:text-rose-600 text-sm font-medium"
                  data-testid="toggle-password"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="text-right">
              <Link to="/forgot-password" className="text-rose-600 hover:text-rose-800 text-sm">
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full h-12 text-lg font-medium shadow-lg shadow-rose-500/30"
              data-testid="login-submit"
            >
              {loading ? t('common.signingIn') : t('common.signIn')}
            </Button>
          </form>

          <p className="text-center text-rose-600 mt-8">
            {t('login.noAccount')}{" "}
            <Link to="/register" className="text-rose-700 font-medium hover:underline" data-testid="register-link">
              {t('common.createAccount')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1714741324312-73bb4b1391d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxtb3NxdWUlMjBhcmNoJTIwYXJjaGl0ZWN0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3NzI0MjgzMjl8MA&ixlib=rb-4.1.0&q=85"
          alt="Mosque Architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-rose-600/70 to-pink-600/70" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-md">
            <span className="text-6xl font-light text-rose-200 block mb-6" style={{ fontFamily: "'Amiri', serif" }}>س</span>
            <h2 className="font-serif text-3xl mb-4">{t('login.sideTitle')}</h2>
            <p className="text-rose-100">
              "{t('login.sideQuote')}"
            </p>
            <p className="text-rose-200 mt-4 text-sm">— {t('login.sideSource')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
